import { useEffect, useId, useState } from 'react';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import './composer-guidance.css';

type BrandChoice = { id: number; name: string };
type Props = {
  choices: BrandChoice[];
  value: number | null;
  userId?: number;
  disabled?: boolean;
  placeholder: string;
  onSelect: (id: number) => void;
  onAdd: () => void;
};

/** Recents are explicit selections in this browser, scoped to the signed-in account. */
export function BrandCombobox({ choices, value, userId, disabled, placeholder, onSelect, onAdd }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<number[]>([]);
  const listId = useId();
  const storageKey = userId == null ? null : `admoji-recent-brand-folders:v1:${userId}`;
  useEffect(() => {
    try {
      const ids: unknown = storageKey ? JSON.parse(localStorage.getItem(storageKey) || '[]') : [];
      setRecent(Array.isArray(ids) ? ids.filter(id => Number.isSafeInteger(id)).slice(0, 3) : []);
    } catch { setRecent([]); }
  }, [storageKey]);
  const selected = choices.find(choice => choice.id === value);
  const recentChoices = recent.flatMap(id => choices.filter(choice => choice.id === id));
  const choose = (id: number) => {
    const next = [id, ...recent.filter(item => item !== id)].slice(0, 3);
    if (storageKey) {
      setRecent(next);
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* Optional history. */ }
    }
    onSelect(id);
    setQuery('');
    setOpen(false);
  };
  const item = (choice: BrandChoice) => (
    <CommandItem data-testid={`home-brand-option-${choice.id}`} key={choice.id} value={String(choice.id)} keywords={[choice.name]} onSelect={() => choose(choice.id)} className="brand-combobox-option">
      <span className="brand-combobox-option-name">{choice.name}</span>
      {choice.id === value && <Check size={15} aria-label="Selected" />}
    </CommandItem>
  );
  return (
    <Popover open={open} onOpenChange={next => { setOpen(next); if (!next) setQuery(''); }}>
      <PopoverTrigger asChild>
        <button id="home-brand-select" data-testid="home-brand-select" data-selected-folder={value ?? ''} type="button" role="combobox" aria-expanded={open} aria-controls={listId} aria-label={`Creating for: ${selected?.name || placeholder}`} disabled={disabled} className="brand-combobox-trigger" title={selected?.name}>
          <span>{selected?.name || placeholder}</span><ChevronDown size={15} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="brand-combobox-popover">
        <Command loop filter={(value, search, keywords) => (keywords?.join(' ') || value).toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()) ? 1 : 0}>
          <CommandInput placeholder="Search brands…" aria-label="Search brands" value={query} onValueChange={setQuery} />
          <CommandList id={listId} aria-label="Brands">
            <CommandEmpty>No brands match your search.</CommandEmpty>
            {!query.trim() && recentChoices.length > 0 && <CommandGroup heading="Recently selected">{recentChoices.map(item)}</CommandGroup>}
            <CommandGroup heading="All brands">{choices.filter(choice => query.trim() || !recent.includes(choice.id)).map(item)}</CommandGroup>
          </CommandList>
        </Command>
        <button type="button" className="brand-combobox-add" onClick={() => { setOpen(false); setQuery(''); onAdd(); }}><Plus size={15} aria-hidden="true" />Add a new brand</button>
      </PopoverContent>
    </Popover>
  );
}
